import { useCompanyId } from '@startuptools/common/react-wrapper';
import { MeetingDocument, useMeetingSuspenseQuery, useSetMeetingDetailsMutation } from '../../graphql/react-operations';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Card } from '@startuptools/ui/Card';
import { DateFormat } from '@startuptools/ui/DateFormat';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@startuptools/ui/IconButton';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Divider } from '@startuptools/ui/Divider';
import {
  type GqlBolagoFile,
  type GqlMeeting,
  GqlMeetingHeldKind,
  GqlMeetingKind,
  GqlScriveDocumentStatus,
  GqlZignedAgreementStatus,
} from '../../graphql/base-types.graphql';
import { Trans, useLingui } from '@lingui/react';
import { MeetingStatusHuman } from './components/MeetingStatusHuman';
import { DateTime } from 'luxon';
import { MeetingHeldKindHuman } from './components/MeetingHeldKindHuman';
import { isNil } from 'lodash-es';
import { AlertDialog } from '../../components/simple-dialogs/AlertDialog';
import { useState } from 'react';
import { EditMeetingDialog } from '../common/components/meeting/EditMeetingDialog';
import { type FormValues } from '../common/components/meeting/MeetingDetailsForm';
import { type SubmitHandler } from 'react-hook-form';
import { meetingLinkEdit } from '../../components/Links';
import { useNavigate } from '../../components/Navigate';

export const MeetingKindHeader = ({ meeting }: { meeting: Pick<GqlMeeting, 'kind' | 'number' | 'status'> }) => {
  switch (meeting.kind) {
    case GqlMeetingKind.BoardMeeting: {
      return <Trans id="Board Meeting {number}" values={{ number: meeting.number }} />;
    }
    case GqlMeetingKind.ShareholdersMeeting: {
      return <Trans id="Annual General Meeting" />;
    }
    case GqlMeetingKind.ExtraShareholdersMeeting: {
      return <Trans id="Extra General Meeting" />;
    }
  }
};

const canEditOrDelete = (meeting: {
  latestMinutesDocument?: Pick<GqlBolagoFile, 'scriveStatus' | 'zignedAgreementStatus'> | null;
}) => {
  const latestDoc = meeting.latestMinutesDocument;
  if (isNil(latestDoc?.scriveStatus) && isNil(latestDoc?.zignedAgreementStatus)) {
    return true;
  }
  if (
    !isNil(latestDoc.scriveStatus) &&
    [
      GqlScriveDocumentStatus.Canceled,
      GqlScriveDocumentStatus.Timedout,
      GqlScriveDocumentStatus.Rejected,
      GqlScriveDocumentStatus.Preparation,
    ].includes(latestDoc.scriveStatus)
  ) {
    return true;
  }
  if (
    !isNil(latestDoc.zignedAgreementStatus) &&
    [GqlZignedAgreementStatus.Open, GqlZignedAgreementStatus.Draft, GqlZignedAgreementStatus.Cancelled].includes(
      latestDoc.zignedAgreementStatus,
    )
  ) {
    return true;
  }
  return false;
};

export const MeetingShowInfo = ({
  meetingId,
  deleteMeeting,
}: {
  meetingId: string;
  deleteMeeting: (meeting: Pick<GqlMeeting, 'id' | 'kind'>) => void;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { navigate } = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLockedEditDialog, setShowLockedEditDialog] = useState(false);
  const [showLockedDeleteDialog, setShowLockedDeleteDialog] = useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [doSetDetails] = useSetMeetingDetailsMutation({ refetchQueries: [MeetingDocument] });
  const {
    data: { meeting },
  } = useMeetingSuspenseQuery({ variables: { companyId, id: meetingId }, fetchPolicy: 'cache-and-network' });

  const startTime = DateTime.fromISO(meeting.startTime);
  const endTime = meeting.endTime ? DateTime.fromISO(meeting.endTime) : null;

  const handleEdit = () => {
    if (!canEditOrDelete(meeting)) {
      setShowLockedEditDialog(true);
      return;
    }
    if (!meeting.usesStepper) {
      setShowEditDialog(true);
      return;
    }
    void navigate(meetingLinkEdit(meeting));
  };

  const handleDelete = () => {
    if (!canEditOrDelete(meeting)) {
      setShowLockedDeleteDialog(true);
      return;
    }
    deleteMeeting(meeting);
  };

  const onEditSubmit: SubmitHandler<FormValues> = async ({ startTime, endTime, meetingTemplateId: _, ...input }) => {
    await doSetDetails({
      variables: {
        companyId,
        input: {
          id: meetingId,
          startTime: startTime.toString(),
          endTime: endTime?.toString() ?? null,
          ...input,
        },
      },
    });
    setShowEditDialog(false);
  };

  return (
    <>
      <Card>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" gap={2}>
              <Typography level="h4">
                <MeetingKindHeader meeting={meeting} />
              </Typography>
              {meeting.usesStepper && (
                <>
                  <Divider orientation="vertical" />
                  <Typography level="h4">
                    <MeetingStatusHuman>{meeting.status}</MeetingStatusHuman>
                  </Typography>
                </>
              )}
            </Stack>
            <ButtonGroup>
              <Button color="primary" onClick={handleEdit}>
                <Trans id="Edit" />
              </Button>
              <IconButton color="danger" onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </ButtonGroup>
          </Stack>

          <Stack>
            <Box>
              {meeting.heldKind !== GqlMeetingHeldKind.PerCapsulam ? (
                <>
                  <Trans
                    id="Time: <time></time>"
                    components={{
                      time: (
                        <DateFormat locale={i18n.locale} format={DateTime.DATETIME_SHORT}>
                          {startTime}
                        </DateFormat>
                      ),
                    }}
                  />
                  {endTime && (
                    <>
                      {' - '}
                      <DateFormat locale={i18n.locale} format={DateTime.TIME_SIMPLE}>
                        {endTime}
                      </DateFormat>
                    </>
                  )}
                </>
              ) : (
                <Trans
                  id="Date: <date></date>"
                  components={{
                    date: (
                      <DateFormat locale={i18n.locale} format={DateTime.DATE_SHORT}>
                        {startTime}
                      </DateFormat>
                    ),
                  }}
                />
              )}
            </Box>
            <Box>
              <Trans
                id="Held: <HeldKind></HeldKind>"
                components={{ HeldKind: <MeetingHeldKindHuman>{meeting.heldKind}</MeetingHeldKindHuman> }}
              />
            </Box>
            {meeting.usesStepper && (
              <>
                {[GqlMeetingHeldKind.Physical, GqlMeetingHeldKind.PhysicalAndPhone].includes(meeting.heldKind) &&
                  meeting.place && (
                    <Box>
                      <Trans id="Place: {place}" values={{ place: meeting.place }} />
                    </Box>
                  )}
                {[GqlMeetingHeldKind.Phone, GqlMeetingHeldKind.PhysicalAndPhone].includes(meeting.heldKind) &&
                  meeting.phone && (
                    <Box>
                      <Trans id="Phone: {phone}" values={{ phone: meeting.phone }} />
                    </Box>
                  )}
              </>
            )}
          </Stack>
        </Stack>
      </Card>
      {showEditDialog && (
        <EditMeetingDialog meeting={meeting} onClose={() => setShowEditDialog(false)} onSubmit={onEditSubmit} />
      )}
      {showLockedEditDialog && (
        <AlertDialog
          title={<Trans id="Not possible to make changes to the meeting" />}
          onAccept={() => setShowLockedEditDialog(false)}
          acceptButtonText="Ok"
        >
          <Trans id="There is an ongoing or finished signature process, cancel it if you want to make changes to the meeting." />
        </AlertDialog>
      )}
      {showLockedDeleteDialog && (
        <AlertDialog
          title={<Trans id="Not possible to delete the meeting" />}
          onAccept={() => setShowLockedDeleteDialog(false)}
          acceptButtonText="Ok"
        >
          <Trans id="There is an ongoing or finished signature process, cancel it if you want to delete the meeting." />
        </AlertDialog>
      )}
    </>
  );
};
